import Lottie from "react-lottie-player";
import anLogin from "../../../assets/animations/an-Login.json";
import { object, string } from "yup";
import {
  CustomButton2,
  CustomCheckBox,
  CustomDropdownSearch,
  CustomInput,
  CustomRadioButtonGroup,
  CustomView,
  ImageUploader,
} from "../../components";

import "./index.scss";
import values from "../../values";
import { ReactNode, useEffect, useRef, useState } from "react";
import { ICityItem, IItem, IProvinceItem } from "../../types/common.interface";
import { Form, Formik } from "formik";
import { useQuery } from "react-query";
import api from "../../api";
import successHandler from "../../utils/successHandler";
import errorHandler from "../../utils/errorHandler";
import validations from "../../validations";
import { uploadImage } from "../../api/method.post/common";
import TitleLine from "../../components/TitleLine";

const {
  loadCaptchaEnginge,
  LoadCanvasTemplate,
  LoadCanvasTemplateNoReload,
  validateCaptcha,
} = require("react-simple-captcha");

type DropdownTypes = {
  errorProvince: string | undefined;
  errorCity: string | undefined;
  province: IProvinceItem | undefined;
  city: ICityItem | undefined;
};

// Define custom error messages in Persian
const customErrorMessages = {
  nationalCode: {
    matches: "کد ملی باید 10 رقم باشد.",
    required: "کد ملی الزامی است.",
  },
  phoneNumber: {
    matches: "شماره تلفن باید با 09 شروع شده و 11 رقم باشد.",
    required: "شماره تلفن الزامی است.",
  },
  shabaNumber: {
    matches: "شماره شبا باید 24 رقم باشد.",
    required: "شماره شبا الزامی است.",
  },
  kartNumber: {
    matches: "شماره کارت باید 16 رقم باشد.",
    required: "شماره کارت الزامی است.",
  },
  firstName: {
    required: "نام الزامی است.",
  },
  companyName: {
    required: "نام الزامی است.",
  },
  lastName: {
    required: "نام خانوادگی الزامی است.",
  },
  address: {
    required: "آدرس الزامی است.",
  },
  captcha: {
    required: "کد امنیتی اشتباه است.",
  },
};

// Define the validation schema with custom error messages
const formSchema = object({
  nationalCode: string()
    .matches(/^\d{10}$/, customErrorMessages.nationalCode.matches)
    .required(customErrorMessages.nationalCode.required),
  phoneNumber: string()
    .matches(/^09\d{9}$/, customErrorMessages.phoneNumber.matches)
    .required(customErrorMessages.phoneNumber.required),
  shabaNumber: string()
    .matches(/^\d{24}$/, customErrorMessages.shabaNumber.matches)
    .required(customErrorMessages.shabaNumber.required),
  kartNumber: string()
    .matches(/^\d{16}$/, customErrorMessages.kartNumber.matches)
    .required(customErrorMessages.kartNumber.required),
  companyName: string().required(customErrorMessages.companyName.required),
  firstName: string().required(customErrorMessages.firstName.required),
  lastName: string().required(customErrorMessages.lastName.required),
  address: string().required(customErrorMessages.address.required),
  nationalId: validations.yupValidations.nationalId,
  registrationNumber: validations.yupValidations.registrationNumber,
  captcha: string().required("وارد کردن کد امنیتی الزامی است"),
});

const RegisterScreen = () => {
  // ** ============================================================================================================
  // ** ------------------------------------------ Start Value Validation ------------------------------------------

  const { Container, Row, Column } = CustomView;
  const [selectedRole, setSelectedRole] = useState<IItem | null>(
    values.topMenuItems.REGISTER_ROLE_ITEM[0]
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [captchaError, setCaptchaError] = useState<boolean>(false);
  const [location, setLocation] = useState<DropdownTypes>({
    errorProvince: undefined,
    errorCity: undefined,
    province: undefined,
    city: undefined,
  });
  const [isDriverWallet, setIsDriverWallet] = useState<boolean>(false);

  const [selectedNationalCardImage, setSelectedNationalCardImage] =
    useState<string>();
  const [selectedNationalCardBackImage, setSelectedNationalCardBackImage] =
    useState<string>();
  const [selectedCompanyLicense, setSelectedCompanyLicenseImage] =
    useState<string>();

  const valueRef = useRef<HTMLInputElement>(null);

  // ** ------------------------------------------- End Value Validation -------------------------------------------
  // ** ============================================================================================================

  // !! ============================================================================================================
  // !! ---------------------------------------------- Start API Call ----------------------------------------------

  const getAllProvince = useQuery([values.useQueryKeys.getAllProvince], () =>
    api.get.common.getAllProvince()
  );
  const getAllCity = useQuery(
    [values.useQueryKeys.getAllCity, location?.province],
    () =>
      api.get.common.getAllCity({
        provinceCode: location?.province ? location?.province?.Code : undefined,
      })
  );

  useEffect(() => {
    loadCaptchaEnginge(4, "lightgray", "black", "numbers"); // loadCaptchaEnginge(Number_Of_Captcha_Charcters, Background_Color, Font_Color, Special_Characters);
  }, []);

  // !! ----------------------------------------------- End API Call -----------------------------------------------
  // !! ============================================================================================================

  // ** ============================================================================================================
  // ** ------------------------------------------ Start Local Functions -------------------------------------------

  async function onSubmitForm(event: any) {
    if (validateCaptcha(event?.captcha)) {
      setIsLoading(true);
      setCaptchaError(false);
      const result = await api.post.common.createCompanyPublic({
        ...event,
        province: location.province,
        city: location.city,
        nationalCardFront: selectedNationalCardImage?.link,
        nationalCardBack: selectedNationalCardBackImage?.link,
        companyLicence: selectedCompanyLicense?.link,
        isDriverWallet,
      });
      console.clear();
      console.log(result);
      if (result?.CODE === 2011) {
        successHandler(2010);
        setTimeout(() => {
          window.location.href = "/panel/login";
        }, 2500);
      } else {
        errorHandler(result?.CODE);
      }
      setIsLoading(false);
    } else {
      setCaptchaError(true);
    }
  }

  const [captcha, setCaptcha] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  // ** ------------------------------------------- End Local Functions --------------------------------------------
  // ** ============================================================================================================

  // ?? ============================================================================================================
  // ?? -------------------------------------------- Start JSX Content ---------------------------------------------
  async function onUploadImage(
    imageFile: File,
    setImage: (image: any) => void
  ) {
    const imagePromise = await uploadImage!(imageFile);
    if (imagePromise?.CODE === 2042) setImage(imagePromise?.result);
  }

  return (
    <section id="register-screen-content">
      <Container fluid>
        <Row>
          <Column large={6}>
            <div className="left-side">
              <div className="login-card">
                <h1>نام نویسی</h1>
                <div
                  style={{
                    pointerEvents: isLoading ? "none" : "all",
                  }}
                >
                  <CustomRadioButtonGroup
                    menus={values.topMenuItems.REGISTER_ROLE_ITEM}
                    selected={selectedRole}
                    onClick={setSelectedRole}
                    canNull={false}
                  />
                </div>
                <Formik
                  initialValues={{
                    nationalCode: "",
                    phoneNumber: "",
                    companyName: "",
                    shabaNumber: "",
                    kartNumber: "",
                    firstName: "",
                    lastName: "",
                    address: "",
                    nationalId: "",
                    registrationNumber: "",
                    captcha: "",
                  }}
                  validationSchema={formSchema}
                  onSubmit={(values) => {
                    if (!location.province?._id) {
                      setLocation({
                        ...location,
                        errorProvince: "انتخاب استان اجباری است",
                      });
                      return;
                    }
                    if (!location.city?._id) {
                      setLocation({
                        ...location,
                        errorCity: "انتخاب شهر اجباری است",
                      });
                      return;
                    }
                    // setIsLoading(true);
                    const formValues = {
                      ...values,
                      nationalCardFront: selectedNationalCardImage,
                      nationalCardBack: selectedNationalCardBackImage,
                      companyLicence: selectedCompanyLicense,
                    };
                    onSubmitForm(formValues);
                  }}
                >
                  {({ values, handleBlur, handleChange, errors, touched }) => (
                    <Form>
                      <div className="form-fields-container">
                        <Row>
                          <Column className="form-inputs" medium={4}>
                            <CustomInput
                              type="text"
                              label={"نام"}
                              name="firstName"
                              value={values.firstName}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.firstName && touched.firstName}
                              errorText={errors.firstName}
                            />
                          </Column>
                          <Column className="form-inputs" medium={4}>
                            <CustomInput
                              type="text"
                              label={"نام خانوادگی"}
                              name="lastName"
                              value={values.lastName}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.lastName && touched.lastName}
                              errorText={errors.lastName}
                            />
                          </Column>
                          <Column className="form-inputs" medium={4}>
                            <CustomInput
                              type="text"
                              maxLength={10}
                              label={"کد ملی"}
                              name="nationalCode"
                              value={values.nationalCode}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={
                                errors.nationalCode && touched.nationalCode
                              }
                              errorText={errors.nationalCode}
                            />
                          </Column>
                          <Column className="form-inputs" medium={4}>
                            <CustomInput
                              type="text"
                              label={"شماره‌ی همراه"}
                              maxLength={11}
                              name="phoneNumber"
                              value={values.phoneNumber}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.phoneNumber && touched.phoneNumber}
                              errorText={errors.phoneNumber}
                            />
                          </Column>
                          <Column className="form-inputs" medium={4}>
                            <CustomInput
                              type="text"
                              label={"نام شرکت"}
                              name="companyName"
                              value={values.companyName}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.companyName && touched.companyName}
                              errorText={errors.companyName}
                            />
                          </Column>
                          <Column medium={4} className="form-inputs">
                            <CustomInput
                              name="registrationNumber"
                              id="registrationNumber"
                              label="شماره ثبت"
                              value={values.registrationNumber}
                              onValueChange={handleChange}
                              formikProps={{ onBlur: handleBlur }}
                              error={
                                errors.registrationNumber &&
                                touched.registrationNumber
                              }
                              errorText={errors.registrationNumber}
                              maxLength={16}
                            />
                          </Column>
                          <Column medium={4} className="form-inputs">
                            <CustomInput
                              name="nationalId"
                              id="nationalId"
                              label="شناسه ملی"
                              value={values.nationalId}
                              onValueChange={handleChange}
                              formikProps={{ onBlur: handleBlur }}
                              error={errors.nationalId && touched.nationalId}
                              errorText={errors.nationalId}
                              maxLength={11}
                            />
                          </Column>
                          <Column className="form-inputs" medium={4}>
                            <label className="form-label">استان</label>
                            <CustomDropdownSearch
                              items={getAllProvince?.data?.result}
                              itemName="Name"
                              defaultItem={location.province}
                              onSelectItem={(event: IProvinceItem) => {
                                setLocation({
                                  ...location,
                                  errorProvince: undefined,
                                  province: event,
                                  city: undefined,
                                });
                              }}
                              placeHolder="انتخاب استان"
                              isLoading={getAllProvince.isLoading}
                              isSearch
                            />
                            <div className="error-div">
                              {location.errorProvince}
                            </div>
                          </Column>
                          <Column className="form-inputs" medium={4}>
                            <label className="form-label">شهر</label>
                            <CustomDropdownSearch
                              items={getAllCity?.data?.result}
                              itemName="Name"
                              defaultItem={location.city}
                              onSelectItem={(event: ICityItem) => {
                                setLocation({
                                  ...location,
                                  errorCity: undefined,
                                  city: event,
                                });
                              }}
                              placeHolder="انتخاب شهر"
                              isLoading={getAllCity.isLoading}
                              isSearch
                            />
                            <div className="error-div">
                              {location.errorCity}
                            </div>
                          </Column>
                          <Column className="form-inputs" medium={3}>
                            <CustomInput
                              type="text"
                              label={"شماره‌ کارت"}
                              name="kartNumber"
                              maxLength={16}
                              value={values.kartNumber}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.kartNumber && touched.kartNumber}
                              errorText={errors.kartNumber}
                            />
                          </Column>
                          <Column className="form-inputs" medium={5}>
                            <CustomInput
                              type="text"
                              label={"شماره‌ی شبا"}
                              maxLength={24}
                              name="shabaNumber"
                              value={values.shabaNumber}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.shabaNumber && touched.shabaNumber}
                              errorText={errors.shabaNumber}
                              isShaba
                            />
                          </Column>
                          <CustomView.Column medium={4} className="form-inputs">
                            <CustomCheckBox
                              isChecked={isDriverWallet}
                              onChecked={() =>
                                setIsDriverWallet((prev) => !prev)
                              }
                              label="پرداخت پول رانندگان به شرکت"
                            />
                          </CustomView.Column>
                          <Column className="form-inputs" medium={12}>
                            <CustomInput
                              type="text"
                              label={"آدرس"}
                              name="address"
                              value={values.address}
                              onValueChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.address && touched.address}
                              errorText={errors.address}
                            />
                          </Column>{" "}
                          <TitleLine title="مدارک شرکت" />
                          <CustomView.Row className="form-row dropdown">
                            {/* {dropDownErrors.images !== "" && (
            <div
              className="dropdown"
              style={{ minHeight: "0.7rem", color: "red", fontSize: "0.7rem" }}
            >
            <div className="">{dropDownErrors.images}</div>
            </div>
          )} */}
                            <CustomView.Column medium={4} className="">
                              <ImageUploader
                                defaultImage={selectedNationalCardImage}
                                setImage={setSelectedNationalCardImage}
                                uploadImage={(event: any) =>
                                  onUploadImage(
                                    event,
                                    setSelectedNationalCardImage
                                  )
                                }
                                label="کارت ملی مدیر شرکت"
                              />
                            </CustomView.Column>{" "}
                            <CustomView.Column medium={4} className="">
                              <ImageUploader
                                defaultImage={selectedNationalCardBackImage}
                                setImage={setSelectedNationalCardBackImage}
                                uploadImage={(event: any) =>
                                  onUploadImage(
                                    event,
                                    setSelectedNationalCardBackImage
                                  )
                                }
                                label="پشت کارت ملی مدیر شرکت"
                              />
                            </CustomView.Column>{" "}
                            <CustomView.Column medium={4} className="">
                              <ImageUploader
                                defaultImage={selectedCompanyLicense}
                                setImage={setSelectedCompanyLicenseImage}
                                uploadImage={(event: any) =>
                                  onUploadImage(
                                    event,
                                    setSelectedCompanyLicenseImage
                                  )
                                }
                                label="جواز شرکت"
                              />
                            </CustomView.Column>{" "}
                          </CustomView.Row>
                          <Column
                            className="form-inputs"
                            id="custom-input-1-container"
                            medium={12}
                          >
                            <>
                              <div className="input-label right">
                                <label>عبارت امنیتی</label>
                                {
                                  <span className="error-div">
                                    {captchaError &&
                                      "(" +
                                        customErrorMessages.captcha.required +
                                        ")"}
                                  </span>
                                }
                              </div>
                              <Row>
                                <Column medium={6}>
                                  <div className="input-block">
                                    <CustomInput
                                      type="text"
                                      id="captcha"
                                      name="captcha"
                                      value={values.captcha}
                                      placeHolder={
                                        "عبارت امنیتی را وارد نمایید"
                                      }
                                      onValueChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.captcha && touched.captcha}
                                      errorText={errors.captcha}
                                      // autoComplete="off"
                                    />
                                    {/* <div className="error-div">{errors.captcha}</div> */}
                                    {/* <input /> */}
                                    {/* {errors.captcha && touched.captcha ? (
                                <p className="form-error">{errors.captcha}</p>
                              ) : null} */}
                                  </div>
                                </Column>
                                <Column
                                  medium={5}
                                  className="reload-captcha-column"
                                >
                                  <LoadCanvasTemplate
                                    reloadText={`بارگذاری مجدد`}
                                  />
                                </Column>
                                <div className=""></div>
                              </Row>
                            </>
                          </Column>
                        </Row>
                        <CustomButton2
                          type="primary"
                          title="ثبت نام در سنجد"
                          isLoading={isLoading}
                          isSubmit
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </Column>
          <Column large={6}>
            <div className="right-side">
              <Lottie
                loop
                animationData={anLogin}
                play
                style={{ width: "100%", height: "90%" }}
              />
            </div>
          </Column>
        </Row>
      </Container>
    </section>
  );
  // ?? --------------------------------------------- End JSX Content ----------------------------------------------
  // ?? ============================================================================================================
};

export default RegisterScreen;
